import { generateImageStaggerTimeline } from "./homepage.ts";
export const timelines: gsap.core.Timeline[] = [];

const tabsBlocks = document.querySelectorAll<HTMLDivElement>("[data-tabs]");

function initTabs(tabsBlock: HTMLDivElement) {
  if (!tabsBlock) return;
  const currentLang = document.documentElement.lang;

  const isHeroTabs = tabsBlock.classList.contains("hero-tabs");

  // Get relevant elements and collections
  const tablist = tabsBlock.querySelector("[role='tablist']") as HTMLUListElement;

  if (!tablist || tablist.children.length <= 0) return; // prevent error if no tabs

  const tabs = tablist.querySelectorAll<HTMLButtonElement>("button[role='tab']");
  const panels = tabsBlock.querySelectorAll<HTMLElement>("[role='tabpanel']");
  const isMobile = !isHeroTabs && window.matchMedia("(max-width: 767px)").matches;
  const mobileToggle = tablist.querySelector<HTMLButtonElement>(".tab-jump-to");
  const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Update mobile toggle text for French
  if (currentLang === "fr" && mobileToggle) {
    mobileToggle.textContent = "Aller à l’onglet";
  }

  if (isHeroTabs) {
    panels.forEach((panel, i) => {
      timelines[i] = generateImageStaggerTimeline(panel);
    });

    if (!prefersReducedMotion) {
      // Auto-switch functionality for hero tabs
      let currentTabIndex = 0;
      let autoSwitchActive = true;

      const stopAutoSwitch = () => {
        if (!autoSwitchActive) return;
        clearInterval(autoSwitchInterval);
        autoSwitchActive = false;
      };

      const switchToNextTab = () => {
        const currentTab = tabs[currentTabIndex];
        currentTabIndex = (currentTabIndex + 1) % tabs.length;
        const nextTab = tabs[currentTabIndex];
        switchTab(currentTab, nextTab);
      };

      const autoSwitchInterval = window.setInterval(switchToNextTab, 6000);

      if (autoSwitchActive) {
        tabs.forEach((tab) => {
          tab.addEventListener("focus", stopAutoSwitch);
        });

        document.addEventListener("visibilitychange", () => {
          if (document.hidden) {
            stopAutoSwitch();
          }
        });

        panels.forEach((panel) => {
          const images = panel.querySelectorAll(".mosaic-image");
          images.forEach((img) => {
            img.addEventListener("mouseenter", stopAutoSwitch);
          });
        });
      }
    }
  }

  const switchTab = (oldTab: HTMLButtonElement, newTab: HTMLButtonElement, event?: Event) => {
    const triggeredByKeyboard = event instanceof KeyboardEvent;

    // Auto focus when switching tabs via keyboard
    if (triggeredByKeyboard) {
      newTab.focus();
    }

    newTab.setAttribute("aria-selected", "true");
    newTab.setAttribute("tabindex", "0");
    newTab.classList.add("active");

    oldTab.setAttribute("aria-selected", "false");
    oldTab.setAttribute("tabindex", "-1");
    oldTab.classList.remove("active");

    // Get the indices of the new and old tabs to find the correct
    // tab panels to show and hide
    const newIndex = Array.from(tabs).indexOf(newTab);
    const oldIndex = Array.from(tabs).indexOf(oldTab);

    const oldPanel = panels[oldIndex];
    const newPanel = panels[newIndex];

    const newTimeline = timelines ? timelines[newIndex] : null;

    oldPanel.classList.remove("active");
    oldPanel.classList.add("closed");

    const onTransitionEnd = () => {
      newPanel.classList.remove("closed");
      newPanel.hidden = false;
      void newPanel.offsetHeight; // force reflow
      newPanel.classList.add("active");
      oldPanel.hidden = true;
      oldPanel.removeEventListener("transitionend", onTransitionEnd);

      if (isHeroTabs) {
        newTimeline?.play(0);
      }

      if (isMobile) {
        const containerRect = tabsBlock.getBoundingClientRect();
        const offset = 100;

        window.scrollTo({
          top: containerRect.top + window.scrollY - offset,
          behavior: "smooth",
        });
      }
    };
    oldPanel.addEventListener("transitionend", onTransitionEnd);

    if (isMobile) {
      tablist.setAttribute("data-mobile-open", "false");
    }
  };

  tabs.forEach((tab, i) => {
    tab.setAttribute("aria-controls", panels[i].id);
    tab.addEventListener("click", (e) => {
      e.preventDefault();
      const target = e.currentTarget as HTMLButtonElement;
      const currentTab = tablist.querySelector("[aria-selected='true']") as HTMLButtonElement;
      if (target !== currentTab) {
        switchTab(currentTab, target, e);
      } else if (isMobile) {
        tablist.setAttribute("data-mobile-open", "true");
      }
    });

    // Handle keydown events for keyboard users
    tab.addEventListener("keydown", (e) => {
      const target = e.currentTarget as HTMLButtonElement;
      const index = Array.from(tabs).indexOf(target);
      const isFirst = index === 0;
      const isLast = index === tabs.length - 1;
      const dir =
        e.key === "ArrowLeft"
          ? isFirst
            ? tabs.length - 1
            : index - 1
          : e.key === "ArrowRight"
          ? isLast
            ? 0
            : index + 1
          : null;

      if (dir !== null) {
        e.preventDefault();
        switchTab(target, tabs[dir] as HTMLButtonElement, e);
      }
    });
  });

  // Add tab panel semantics and hide them all
  panels.forEach((panel, i) => {
    panel.setAttribute("aria-labelledby", tabs[i].id);
    panel.hidden = true;
  });

  // Initially activate the first tab and reveal the first tab panel
  const initialTab = 0;
  tabs[initialTab].setAttribute("tabindex", "0");
  tabs[initialTab].setAttribute("aria-selected", "true");
  panels[initialTab].hidden = false;
  tabs[initialTab].classList.add("active");
  panels[initialTab].classList.add("active");

  if (isMobile) {
    mobileToggle?.addEventListener("click", (e) => {
      tablist.setAttribute("data-mobile-open", "false");
    });
  }
}

tabsBlocks.forEach(initTabs);
